import { FormSize, FormWrapper, TextArea, TextInput } from '@ardoq/forms';
import { ModalLayout, ModalSize, ModalTemplate, modal } from '@ardoq/modal';
import {
  TAG_NAME_VALIDATION_MESSAGE,
  ardoqValidation,
  validate,
} from '@ardoq/validation';
import { useState } from 'react';
import { dispatchAction } from '@ardoq/rxbeach';
import { createTag, updateTag } from 'streams/tags/TagActions';
import { APITagAttributes, type ArdoqId } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

type TagFormPayload = {
  name: string;
  description: string | null;
};

type TagEditorProps = {
  tag?: TagFormPayload;
  headerText: string;
  onClose: VoidFunction;
  onSubmit: (tag: TagFormPayload) => void;
  i18nLocale: I18nLocale;
};

const TagEditor = ({
  tag,
  headerText,
  onClose,
  onSubmit,
  i18nLocale,
}: TagEditorProps) => {
  const [name, setName] = useState<string>(tag?.name || '');
  const [description, setDescription] = useState<string>(
    tag?.description || ''
  );

  const nameValidation = validate(
    ardoqValidation.getTagNameValidatorDescriptor(name)
  );

  return (
    <ModalTemplate
      modalSize={ModalSize.S}
      headerText={headerText}
      primaryButtonText={t(
        i18nLocale,
        'tabview:tagscape.tagEditor.save',
        'Save'
      )}
      primaryButtonClickId="confirm-save"
      isPrimaryButtonDisabled={!nameValidation.isValid}
      onPrimaryButtonClick={() => {
        onSubmit({ name, description });
        onClose();
      }}
      secondaryButtonText={t(
        i18nLocale,
        'tabview:tagscape.tagEditor.cancel',
        'Cancel'
      )}
      onSecondaryButtonClick={() => onClose()}
    >
      <ModalLayout>
        <FormWrapper formSize={FormSize.AUTO}>
          <TextInput
            label={t(
              i18nLocale,
              'tabview:tagscape.tagEditor.nameLabel',
              'Name'
            )}
            data-click-id="enter-tag-name"
            value={name}
            onValueChange={setName}
            helperText={TAG_NAME_VALIDATION_MESSAGE}
            errorMessage={
              name && !nameValidation.isValid
                ? nameValidation.errorMessages[0]
                : undefined
            }
          />
          <TextArea
            label={t(
              i18nLocale,
              'tabview:tagscape.tagEditor.descriptionLabel',
              'Description'
            )}
            value={description}
            onValueChange={setDescription}
          />
        </FormWrapper>
      </ModalLayout>
    </ModalTemplate>
  );
};

export const editTagModal = ({ tag }: { tag: APITagAttributes }) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders modal() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  modal<false>(resolve => (
    <TagEditor
      onSubmit={({ name, description }) => {
        dispatchAction(updateTag({ ...tag, name, description }));
      }}
      headerText={t(
        i18nLocale,
        'tabview:tagscape.tagEditor.editHeaderText',
        { name: tag.name },
        'Edit {{name}}'
      )}
      tag={tag}
      onClose={() => resolve(false)}
      i18nLocale={i18nLocale}
    />
  ));
};

export const createTagModal = (rootWorkspace: ArdoqId) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders modal() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  modal<false>(resolve => (
    <TagEditor
      headerText={t(
        i18nLocale,
        'tabview:tagscape.tagEditor.createHeaderText',
        'Create tag'
      )}
      onSubmit={({ name, description }) => {
        dispatchAction(
          createTag({
            name,
            description,
            rootWorkspace,
            components: [],
            references: [],
          })
        );
      }}
      onClose={() => resolve(false)}
      i18nLocale={i18nLocale}
    />
  ));
};
